import React, { useState } from 'react';

interface Task {
  id: string;
  number: number;
  title: string;
  description: string;
  requirements: Array<{ text: string; subitems?: string[] }>;
  verification: string[];
}

const tasks: Task[] = [
  {
    id: 'task-1',
    number: 1,
    title: 'Configure S3 CLI for Spaces',
    description: 'Set up awscli or s3cmd to connect to DigitalOcean Spaces using environment variables injected by the lab.',
    requirements: [
      { text: 'Source /etc/dolabs/env to load SPACES_* variables' },
      { text: 'Use an S3-compatible CLI already on the VM, or install one if needed', subitems: ['awscli is provisioned for this lab; s3cmd is also acceptable if you prefer it.'] },
      { text: 'Configure awscli or s3cmd with endpoint and region', subitems: ['Endpoint: $SPACES_ENDPOINT', 'Region: $SPACES_REGION'] },
      { text: 'Do not hardcode credentials; use environment variables' },
      { text: 'Verify you can list the bucket (if available) without errors' },
      { text: 'Document configuration in a short README at /root/spaces_config.md', subitems: ['Record the exact command(s) you used and reference $SPACES_ENDPOINT, $SPACES_BUCKET, and $SPACES_REGION.', 'State explicitly whether you configured awscli or s3cmd so graders can confirm.'] },
    ],
    verification: [
      'CLI configured with endpoint and region',
      'Credentials provided via environment variables',
      'Can run a no-op/list command without errors',
      'Configuration notes documented'
    ],
  },
  {
    id: 'task-2',
    number: 2,
    title: 'Create Backup Script with Encryption',
    description: 'Write an executable script to pack, encrypt, and upload backups to Spaces, and mirror artifacts locally for grading.',
    requirements: [
      { text: 'Create /usr/local/bin/backup_to_spaces.sh (chmod 0755)' },
      { text: 'Pack /var/app/data into a compressed tar archive' },
      { text: 'Encrypt the archive using OpenSSL (AES-256) or GPG' },
      {
        text: 'Create backup_manifest.json with marker {"marker":"do-labs-backup-ok"}',
        subitems: [
          `Example JSON (exact, case- and quote-sensitive): {"marker":"do-labs-backup-ok"}`,
          `Write example: HOST=$(hostname); DATE=$(date +%F); MIRROR="/var/spaces_mirror/backups/$HOST/$DATE"; mkdir -p "$MIRROR"; printf %s\\n '{"marker":"do-labs-backup-ok"}' > "$MIRROR/backup_manifest.json"`,
          `Verify example: grep -R -q '"marker":"do-labs-backup-ok"' /var/spaces_mirror/backups`
        ]
      },
      { text: 'Upload to s3://$SPACES_BUCKET/backups/$HOSTNAME/$DATE/' },
      { text: 'Mirror the same artifacts to /var/spaces_mirror/backups/$HOSTNAME/$DATE/' },
      { text: 'Append success entry to /var/log/backup.log', subitems: ['Log the exact line "BACKUP SUCCESS" so automated grading can detect completion.'] },
      { text: 'Run the backup script manually once before grading', subitems: ['This creates the encrypted archive, manifest, local mirror files, and backup log evidence.'] },
    ],
    verification: [
      'Script is executable at /usr/local/bin/backup_to_spaces.sh',
      'Encrypted file exists in local mirror',
      'Manifest with marker exists in local mirror',
      'Backup log contains success line'
    ],
  },
  {
    id: 'task-3',
    number: 3,
    title: 'Add Retention Policy',
    description: 'Keep only the latest 7 daily backups for the host prefix and remove older ones.',
    requirements: [
      { text: 'Implement a retention block in the backup script', subitems: ['Define RETENTION_COUNT=7 near the top of the script.', 'Use that constant when purging both local and Spaces backups.'] },
      { text: 'Support a DRY-RUN mode before deletion', subitems: ['Use a visible variable such as DRY_RUN=1 or a --dry-run flag so graders can detect the safety mode.'] },
      { text: 'Delete only the oldest backups beyond 7 most recent' },
      { text: 'Log retention actions to /var/log/backup.log', subitems: ['Include the literal text "Retention prune complete" after deletions.'] },
    ],
    verification: [
      'Backup script contains retention logic (find/aws s3 rm/s3cmd del)',
      'Retention actions are logged'
    ],
  },
  {
    id: 'task-4',
    number: 4,
    title: 'Schedule the Backup',
    description: 'Configure a nightly schedule using cron or a systemd timer to run backups automatically.',
    requirements: [
      { text: 'Add a nightly cron entry or a systemd timer unit', subitems: ['Name the cron file /etc/cron.d/spaces-backup or the systemd units spaces-backup.service/spaces-backup.timer so graders can detect them.'] },
      { text: 'If you use cron, call /usr/local/bin/backup_to_spaces.sh from /etc/cron.d/spaces-backup' },
      { text: 'Ensure the schedule preserves environment variables (wrap with /etc/profile sourcing if needed)' },
      { text: 'Verify the scheduler is active/enabled' },
    ],
    verification: [
      'Cron entry or systemd timer present',
      'Scheduler enabled and visible via listing'
    ],
  },
  {
    id: 'task-5',
    number: 5,
    title: 'Restore and Verify',
    description: 'Create a restore helper that fetches the latest backup (Spaces or local mirror), decrypts it, and validates the manifest marker.',
    requirements: [
      { text: 'Create /usr/local/bin/restore_from_spaces.sh (chmod 0755)' },
      { text: 'Download latest artifacts (or read from local mirror)' },
      { text: 'Decrypt the archive into a temp directory' },
      { text: 'Verify backup_manifest.json contains the required marker' },
      { text: 'Write "manifest OK" to /var/log/restore_verify.log on success' },
      { text: 'Run the restore helper once before grading' },
      { text: 'Document the restore flow at /root/restore_notes.md', subitems: ['Include the phrase "manifest OK" in the notes after a successful restore verification.'] },
    ],
    verification: [
      'Restore helper exists and is executable',
      'Restore verification log contains manifest OK',
      'End-to-end process is documented in /root/restore_notes.md'
    ],
  },
];

export default function SpacesBackupAutomationInstructions() {
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [completedChecks, setCompletedChecks] = useState<Set<string>>(new Set());
  const [completedTasks, setCompletedTasks] = useState<Set<number>>(new Set());

  const handleTaskClick = (index: number) => setCurrentTaskIndex(index);
  const handleNextTask = () => setCurrentTaskIndex((i) => (i < tasks.length - 1 ? i + 1 : 0));
  const handlePreviousTask = () => setCurrentTaskIndex((i) => (i > 0 ? i - 1 : 0));

  const toggleVerification = (taskId: string, checkIndex: number) => {
    const id = `${taskId}-check-${checkIndex}`;
    const next = new Set(completedChecks);
    next.has(id) ? next.delete(id) : next.add(id);
    setCompletedChecks(next);

    const currentTask = tasks[currentTaskIndex];
    const allDone = currentTask.verification.every((_, idx) => next.has(`${currentTask.id}-check-${idx}`));
    if (allDone && !completedTasks.has(currentTaskIndex)) {
      const t = new Set(completedTasks);
      t.add(currentTaskIndex);
      setCompletedTasks(t);
    }
  };

  const currentTask = tasks[currentTaskIndex];
  const progressPercentage = ((currentTaskIndex + 1) / tasks.length) * 100;

  return (
    <div className="bg-[#232937] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#10b981] to-[#059669] px-8 py-6">
        <h2 className="text-2xl font-semibold text-[#1a1f2e]">Instructions</h2>
      </div>

      {/* Content */}
      <div className="flex h-[1800px]">
        {/* Sidebar */}
        <div className="w-[280px] bg-[#1a1f2e] border-r border-[#2d3748] overflow-y-auto">
          {tasks.map((task, index) => (
            <div
              key={task.id}
              onClick={() => handleTaskClick(index)}
              className={`flex items-center px-5 py-4 cursor-pointer border-b border-[#2d3748] hover:bg-[#2d3748] transition-all relative ${
                index === currentTaskIndex ? 'bg-[#2d3748] border-l-[3px] border-l-[#10b981] pl-[17px]' : ''
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mr-3 flex-shrink-0 ${
                completedTasks.has(index) ? 'bg-[#059669]' : 'bg-[#10b981]'
              } text-[#1a1f2e]`}>
                {task.number}
              </div>
              <div className={`text-sm ${index === currentTaskIndex ? 'text-[#e2e8f0] font-medium' : 'text-[#cbd5e1]'}`}>
                {task.title}
              </div>
              {completedTasks.has(index) && (
                <span className="absolute right-5 text-[#10b981] font-bold text-sm">✓</span>
              )}
            </div>
          ))}
        </div>

        {/* Main */}
        <div className="flex-1 p-8 bg-[#1e293b] overflow-y-auto">
          <h3 className="text-[22px] font-semibold text-[#e2e8f0] mb-4">
            Task {currentTask.number}: {currentTask.title}
          </h3>
          <p className="text-[#94a3b8] mb-8 leading-[1.6] text-[15px]">{currentTask.description}</p>

          <div className="mb-8">
            <h4 className="text-[#10b981] text-xs uppercase tracking-[1px] mb-4 font-semibold flex items-center">
              <span className="w-5 h-[2px] bg-[#10b981] mr-[10px]"></span>
              What You Need To Do
            </h4>
            {currentTask.requirements.map((r, i) => (
              <div key={i} className="bg-[#232937] border border-[#2d3748] rounded-lg p-4 mb-3 hover:border-[#374151] hover:shadow-md transition-all">
                <div className="text-[#e2e8f0] text-sm leading-[1.8]">
                  <span className="font-medium">{i + 1}. {r.text}</span>
                  {r.subitems && (
                    <ul className="mt-2 ml-5 space-y-1">
                      {r.subitems.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#10b981] mr-2 mt-0.5">•</span>
                          <span className="text-[#cbd5e1]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-[#232937] border-2 border-dashed border-[#475569] rounded-lg p-5 mt-6">
            <div className="flex items-center text-[#10b981] font-semibold mb-4 text-base">
              <span className="w-5 h-5 bg-[#10b981] text-[#1a1f2e] rounded-full flex items-center justify-center text-xs mr-[10px]">✓</span>
              Verification
            </div>
            {currentTask.verification.map((check, idx) => (
              <div
                key={idx}
                onClick={() => toggleVerification(currentTask.id, idx)}
                className="flex items-center p-[10px_12px] bg-[#1a1f2e] rounded-md mb-2 cursor-pointer hover:bg-[#1e293b] hover:translate-x-[3px] transition-all last:mb-0"
              >
                <div className={`w-[18px] h-[18px] border-2 border-[#10b981] rounded flex items-center justify-center mr-3 transition-all ${
                  completedChecks.has(`${currentTask.id}-check-${idx}`) ? 'bg-[#10b981]' : ''
                }`}>
                  {completedChecks.has(`${currentTask.id}-check-${idx}`) && (
                    <span className="text-[#1a1f2e] text-xs font-bold">✓</span>
                  )}
                </div>
                <span className="text-[#cbd5e1] text-sm">{check}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-6 py-4 bg-[#1a1f2e] border-t border-[#2d3748]">
        <div className="text-[13px] text-[#94a3b8]">Progress: Task {currentTaskIndex + 1} of {tasks.length}</div>
        <div className="w-[200px] h-[6px] bg-[#2d3748] rounded-[3px] overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#10b981] to-[#34d399] transition-all duration-500 rounded-[3px]" style={{ width: `${progressPercentage}%` }} />
        </div>
        <div className="flex gap-[10px]">
          <button onClick={handlePreviousTask} disabled={currentTaskIndex === 0} className="px-4 py-2 bg-[#2d3748] border border-[#374151] text-[#cbd5e1] rounded-md text-[13px] cursor-pointer transition-all hover:bg-[#374151] hover:text-[#e2e8f0] disabled:opacity-50 disabled:cursor-not-allowed">
            Previous
          </button>
          <button onClick={handleNextTask} className="px-4 py-2 bg-[#10b981] border border-[#10b981] text-[#1a1f2e] font-semibold rounded-md text-[13px] cursor-pointer transition-all hover:bg-[#059669] hover:border-[#059669]">
            {currentTaskIndex === tasks.length - 1 ? 'Return to Task 1' : 'Next Task'}
          </button>
        </div>
      </div>
    </div>
  );
}
